import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CimClass, Connectivity, ConnectivityDto, EquipmentRules, EquipmentStatus, EquipmentTemplateDto, PropertyType, SubstationTemplateDto } from '../../../../models/network-structure.model';
import { Voltage } from '../../../../enums/voltage';  
import { ToastrService } from 'ngx-toastr';
import { EquipmentTemplateService } from '../../../../services/equipment-template.service';
import { UtilitySharedService } from '../../../../services/shared//utility-shared.service';
import { SectionDefinitionService } from '../../../../services/section-definition.service';
import { SectionDefinitionEntityType } from '../../../../models/section-definition.model';
import { NodeType } from '../../../../enums/node-type';
import { PermissionsService } from '../../../../services/permissions.service';

@Component({
  selector: 'lib-power-transformer-form',
  templateUrl: './power-transformer-form.component.html',
  styleUrls: ['./power-transformer-form.component.scss'],
  standalone: false
})
export class PowerTransformerFormComponent implements OnChanges {
  @Input() equipment!: EquipmentTemplateDto;
  @Input() substation!: SubstationTemplateDto;
  @Input() editEnabled: boolean = false;
  @Input() deleteClicked!: boolean;
  @Output() equipmentChanged = new EventEmitter<void>();
  @Output() saveClicked = new EventEmitter<EquipmentTemplateDto>();

  Connectivity = Connectivity;
  NodeType = NodeType;
  PropertyType = PropertyType;
  Voltage = Voltage;

  connectivitySourceOptions: EquipmentTemplateDto[] = [];
  connectivityDestinationOptions: EquipmentTemplateDto[] = [];

  connectivitySourceInitialValue: EquipmentTemplateDto[] = [];
  connectivityDestinationInitialValue: EquipmentTemplateDto[] = [];

  utilityId: number;

  constructor(
    private toastr: ToastrService, 
    private eqTemplateService: EquipmentTemplateService, 
    private utilitySharedService: UtilitySharedService,
    public permissionsService: PermissionsService
  ) {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['equipment']) {
      this.getConnectivityObjects();
      this.updateFilteredLists();
      
      this.editEnabled = this.equipment.isNew;
    }

    if (changes['deleteClicked'] && !changes['deleteClicked'].firstChange) {
      if (this.equipment.id) {
        const obj = {
          id: this.equipment.id,
          utilityId: this.utilityId
        }
        this.eqTemplateService.delete(obj).subscribe({
          next: (response: any) => {
            if (response?.body?.status == 200 || response?.body?.status == 'OK') {
              this.toastr.success(response?.body?.message);        
              setTimeout(() => {      
                window.location.reload();
              }, 1000);
            }
            else {
              this.toastr.error(response?.body?.message);
            }
          }
        });
      } else {
        this.toastr.info("You can't delete this equipment, because it's not saved.");
      }
    }
  }

  private updateFilteredLists() {
    const allOptions = this.substation.equipmentTemplates.filter(
      eq => eq.guid !== this.equipment.guid && !eq.isNew
    );
    
    this.connectivitySourceOptions = allOptions.filter(
      eq => !this.connectivityDestinationInitialValue.some(dest => dest.guid === eq.guid)
    );
    
    this.connectivityDestinationOptions = allOptions.filter(
      eq => !this.connectivitySourceInitialValue.some(src => src.guid === eq.guid)
    );
  }

  private getConnectivityObjects() {
    const sourceConnectivities = this.equipment.connectivities?.filter(
      c => c.nodeType?.toString() == 'Source'
    ) || [];

    this.connectivitySourceInitialValue = this.substation.equipmentTemplates.filter(
      eq => sourceConnectivities.some(c => c.guid === eq.guid)
    );
    
    const destConnectivities = this.equipment.connectivities?.filter(
      c => c.nodeType?.toString() == 'Destination'
    ) || [];

    this.connectivityDestinationInitialValue = this.substation.equipmentTemplates.filter(
      eq => destConnectivities.some(c => c.guid === eq.guid)
    );
  }

  private getConnectivityByNodeType(nodeType: NodeType): ConnectivityDto | null {
    if (!this.equipment.connectivities || this.equipment.connectivities.length === 0) {
      return null;
    }
    
    return this.equipment.connectivities.find(c => c.nodeType === nodeType) || null;
  }

  private ensureConnectivitiesArray() {
    if (!this.equipment.connectivities) {
      this.equipment.connectivities = [];
    }
  }
  
  onSelectPrimaryVoltage(obj: Voltage) {
    this.equipment.primaryVoltage = obj;
  }
  
  onSelectSecondaryVoltage(obj: Voltage) {
    this.equipment.secondaryVoltage = obj;
  }
  
  onSelectConnectivitySource(selectedEquipments: EquipmentTemplateDto[]) {
    this.ensureConnectivitiesArray();
    
    this.equipment.connectivities = this.equipment.connectivities.filter(
      c => c.nodeType !== NodeType.Source
    );
    
    if (selectedEquipments && selectedEquipments.length > 0) {
      selectedEquipments.forEach(eq => {
        const newConnectivity = new ConnectivityDto();
        newConnectivity.guid = eq.guid;
        newConnectivity.utilityId = this.utilityId;
        newConnectivity.nodeType = NodeType.Source;
        newConnectivity.equipmentTemplateId = eq.id;
        this.equipment.connectivities.push(newConnectivity);
      });
    }
    
    this.connectivitySourceInitialValue = [...selectedEquipments];
    
    const allOptions = this.substation.equipmentTemplates.filter(
      eq => eq.guid !== this.equipment.guid && !eq.isNew
    );
    this.connectivityDestinationOptions = allOptions.filter(
      eq => !selectedEquipments.some(src => src.guid === eq.guid)
    );
    
    this.notifySchematicDiagram();
  }

  onSelectConnectivityDestination(selectedEquipments: EquipmentTemplateDto[]) {
    this.ensureConnectivitiesArray();
    
    this.equipment.connectivities = this.equipment.connectivities.filter(
      c => c.nodeType !== NodeType.Destination
    );
    
    if (selectedEquipments && selectedEquipments.length > 0) {
      selectedEquipments.forEach(eq => {
        const newConnectivity = new ConnectivityDto();
        newConnectivity.guid = eq.guid;
        newConnectivity.utilityId = this.utilityId;
        newConnectivity.nodeType = NodeType.Destination;
        newConnectivity.equipmentTemplateId = eq.id;
        this.equipment.connectivities.push(newConnectivity);
      });
    }
    
    this.connectivityDestinationInitialValue = [...selectedEquipments];
    
    const allOptions = this.substation.equipmentTemplates.filter(
      eq => eq.guid !== this.equipment.guid && !eq.isNew
    );
    this.connectivitySourceOptions = allOptions.filter(
      eq => !selectedEquipments.some(dest => dest.guid === eq.guid)
    );
    
    this.notifySchematicDiagram();
  }

  private save(obj: EquipmentTemplateDto) {
    this.saveClicked.emit(obj);
    this.toastr.success("Equipment saved successfully.");
  }

  onSave() {
    if (this.equipment.className == '') {
      this.toastr.warning("Class Name field is mandatory.");
      return;
    }
    else if (!/^[\p{L}\s]+$/u.test(this.equipment.className.trim())) {
      this.toastr.warning("Class Name field must contain only letters.");
      return;
    }
    else if (this.equipment.primaryVoltage == null) {
      this.toastr.warning("Primary voltage field is mandatory.");
      return;
    }
    else if (this.equipment.secondaryVoltage == null) {
      this.toastr.warning("Secondary voltage field is mandatory.");
      return;
    }
    else if (!this.equipment.connectivities || this.equipment.connectivities.length === 0) {
      this.toastr.warning("At least one winding (Source or Destination) is mandatory.");
      return;
    }
    else if (this.equipment.visualizationConfiguration.symbol == null) {
      this.toastr.warning("Symbol is mandatory.");
      return;
    }
    else {
      this.equipment.status = EquipmentStatus.Complete;
      this.save(this.equipment);
    }
  }

  notifySchematicDiagram() {
    this.equipmentChanged.emit();
  }
}